import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import LiveStrip from "../components/LiveStrip";
import FloatingSupportChat from "../components/FloatingSupportChat";
import HeathrowLiveStatusCompact from "../components/HeathrowLiveStatusCompact";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import PremiumContactStrip from "../components/PremiumContactStrip";
import PostcodeInput from "../components/PostcodeInput";
import api from "../utils/api";
import { trackFunnelEvent } from "../utils/funnelTracking";
import { RouteIntelligencePreview2, RoutePricePreview2 } from "../components/RoutePreview2";
import "./Home.css";

const GOOGLE_REVIEW_URL = "https://share.google/trIAb3GAvw55L1l8S";

const trustBadges = [
  {
    icon: "✓",
    eyebrow: "GOV.UK registered operator",
    title: "Licensed & insured",
    body: "PSV licence PF2071924",
  },
  {
    icon: "G",
    eyebrow: "Google reviews",
    title: "4.8 ★★★★★",
    body: "View our reviews",
    href: GOOGLE_REVIEW_URL,
  },
];

const heroBenefits = [
  { icon: "£", title: "Fixed prices", body: "No hidden fees" },
  { icon: "○", title: "Free waiting", body: "60 minutes" },
  { icon: "✈", title: "Flight monitoring", body: "We track your flight" },
  { icon: "☎", title: "24/7 support", body: "Always here" },
];

const operatorCards = [
  {
    icon: "♔",
    eyebrow: "GOV.UK registered",
    title: "Licensed & insured",
    body: "Public Carrier Licence",
  },
  {
    icon: "G",
    eyebrow: "Google reviews",
    title: "4.8 ★★★★★",
    body: "View our reviews",
    href: GOOGLE_REVIEW_URL,
  },
  {
    icon: "👥",
    eyebrow: "1000s of journeys",
    title: "Completed",
    body: "Across the UK",
  },
  {
    icon: "♬",
    eyebrow: "24/7 support",
    title: "Always here",
    body: "Before, during & after",
  },
];

const reasonCards = [
  { icon: "✓", title: "Punctual & reliable", body: "98% on-time record" },
  { icon: "▣", title: "Modern, clean fleet", body: "8 to 16 seaters" },
  { icon: "👤", title: "Professional drivers", body: "Fully licensed & vetted" },
  { icon: "✦", title: "Flexible & friendly", body: "We adapt to you" },
];

const intelligenceCards = [
  {
    title: "Airport timing",
    body: "Plan pickup times around airport transfers, terminal routes and group luggage needs.",
  },
  {
    title: "Road awareness",
    body: "Use the same journey-intelligence layer already used across the CheapCabGrab airport tools.",
  },
  {
    title: "Group fit",
    body: "Start with minibus-sized quotes so 8 and 16 seater options are relevant from the first search.",
  },
  {
    title: "Booking handoff",
    body: "Move from quote to results and booking without a separate backend or new database flow.",
  },
];

const operatorTrustItems = [
  {
    title: "RHP Minibus Hire UK Ltd",
    body: "Transport support from an active UK company listed at Companies House under company number 10377899.",
  },
  {
    title: "PSV operator licence PF2071924",
    body: "Licence reference can be checked through GOV.UK's public vehicle operator licence search.",
  },
  {
    title: "Licensed carriers",
    body: "Companies House lists the business activity as licensed carriers, supporting professional group transport.",
  },
];

const tripTypes = [
  "Heathrow, Gatwick, Luton and Stansted transfers",
  "London hotel shuttles and airport meetups",
  "Family holidays, events, teams and group days out",
  "Multi-stop journeys with luggage-ready vehicles",
];

const fleetOptions = [
  {
    title: "8 passenger / 9-seater minibus",
    body: "Best for families and small groups who need more space than an MPV, with room for Heathrow airport luggage.",
  },
  {
    title: "14 to 16 passenger minibus",
    body: "A practical option for teams, larger families, event travel and coordinated Heathrow transfers.",
  },
  {
    title: "Need a coach?",
    body: "For 25+ seats, CheapCoachGrab can handle larger group enquiries through the shared quote platform.",
    link: "/coach-hire",
  },
];

const localHeathrowRoutes = [
  { title: "Slough to Heathrow minibus", href: "/slough-to-heathrow-minibus" },
  { title: "Windsor to Heathrow minibus" },
  { title: "Burnham to Heathrow minibus" },
  { title: "Taplow to Heathrow minibus" },
  { title: "Maidenhead to Heathrow minibus" },
  { title: "Langley to Heathrow minibus" },
  { title: "Marlow to Heathrow minibus" },
  { title: "Henley to Heathrow minibus" },
];

const driverTrustItems = [
  {
    title: "Licensed and vetted drivers",
    body: "Drivers are council licensed, DBS checked where required and assigned through approved licensed operator processes.",
  },
  {
    title: "Airport-specialist timing",
    body: "Heathrow, Gatwick, Luton and Stansted journeys are planned around flight tracking, terminal routes and group loading time.",
  },
  {
    title: "Professional conduct",
    body: "Expect clear route planning, help with luggage, clean vehicles, respectful service and fixed fares with no surge pricing.",
  },
  {
    title: "Safeguarding awareness",
    body: "Licensed group transport work includes safe conduct, disability awareness and support for vulnerable passengers where relevant.",
  },
];

const faqItems = [
  {
    question: "Can we book a minibus for airport pickups?",
    answer:
      "Yes. MinibusGrab is built for airport transfers, hotel pickups, family holidays, teams and group travel with luggage.",
  },
  {
    question: "Do you handle delayed flights?",
    answer:
      "For airport bookings, flight details can be used in the booking flow so pickup planning can account for delays where possible.",
  },
  {
    question: "Can we request child seats or extra luggage space?",
    answer:
      "Add the details when booking or contact support before travel. Vehicle allocation depends on passengers, luggage and route needs.",
  },
  {
    question: "Can larger groups book more than one vehicle?",
    answer:
      "Yes. Larger groups can use multiple minibuses or move to coach hire when a 25+ seat option is a better fit.",
  },
];

const destinationGroups = [
  {
    title: "Airports",
    body: "Heathrow, Gatwick, Luton, Stansted and other UK airport journeys for groups travelling with luggage.",
  },
  {
    title: "Hotels",
    body: "Hotel shuttles, airport corridor hotels, London hotels and multi-room group arrivals.",
  },
  {
    title: "Attractions",
    body: "Group travel to Windsor Castle, Legoland, Thorpe Park, central London sights and event venues.",
  },
];

const pad = (value) => String(value).padStart(2, "0");

const fallbackHeathrowLive = {
  code: "LHR",
  name: "Heathrow",
  overall: { label: "Checking", reason: "Live Heathrow status is updating." },
  hero_status: {
    road: { label: "Checking", headline: "Road and motorway checks are updating." },
    rail: { label: "Checking", headline: "Rail and TfL checks are updating." },
    flights: { label: "Checking", headline: "Flight disruption checks are updating." },
    weather: { label: "Checking", headline: "Weather checks are updating." },
  },
  summary: { headline: "Live sources are checking." },
  preview: {
    terminal: "Terminals checking",
    terminal_pressure: [],
    terminal_queues: [],
    queues: ["Security and border queue checks are updating."],
    weather: "Weather checking",
  },
};

const localDatePartsInHours = (hoursAhead) => {
  const date = new Date(Date.now() + hoursAhead * 60 * 60 * 1000);
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const extractPostcode = (text = "") => {
  const upper = text.toUpperCase();
  const match = upper.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/);
  if (match) return match[0].replace(/\s+/g, "");
  if (upper.includes("HEATHROW") || upper.includes("LHR")) return "TW6";
  return text.trim();
};

const statusToneKey = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("critical") || text.includes("severe") || text.includes("high") || text.includes("red") || text.includes("poor")) return "red";
  if (text.includes("moderate") || text.includes("busy") || text.includes("amber") || text.includes("watch") || text.includes("delay")) return "amber";
  if (text.includes("good") || text.includes("green") || text.includes("clear") || text.includes("normal") || text.includes("on time")) return "green";
  return "checking";
};

const cleanLiveText = (value, fallback = "Live status updating") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  return text.length > 92 ? `${text.slice(0, 89).trim()}...` : text;
};

const statusRank = (value) => {
  const tone = statusToneKey(value);
  if (tone === "red") return 4;
  if (tone === "amber") return 3;
  if (tone === "green") return 2;
  return 1;
};

const queueWaitRank = (wait = "") => {
  const text = String(wait || "").toLowerCase();
  const ranges = [...text.matchAll(/(\d+)\s*-\s*(\d+)/g)].map((match) => Number(match[2]));
  const singles = [...text.matchAll(/(?:^|[^\d])(\d+)(?:\s*\+|\s*min|$)/g)].map((match) => Number(match[1]));
  const lessThan = text.match(/<\s*(\d+)/);
  if (ranges.length) return Math.max(...ranges);
  if (singles.length) return Math.max(...singles);
  if (lessThan) return Number(lessThan[1]) - 1;
  return 0;
};

const queueLineText = (line = {}) => `${line.label || "Queue"}: ${line.wait || "checking"}`;

const formatQueueLines = (lines = []) => {
  const cleanLines = Array.isArray(lines) ? lines.filter(Boolean) : [];
  if (!cleanLines.length) return "Security and border snapshot";
  const sorted = cleanLines
    .slice()
    .sort((a, b) => queueWaitRank(b.wait) - queueWaitRank(a.wait));
  const worst = sorted[0];
  const rest = cleanLines.filter((line) => line !== worst);
  return [worst, ...rest].map(queueLineText).join(" • ");
};

const deriveTerminalLiveStatus = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure : [];
  const strongest = structured
    .slice()
    .sort((a, b) => statusRank(b?.label || b?.level) - statusRank(a?.label || a?.level))[0];
  if (strongest) {
    return {
      label: strongest.label || strongest.level || "Checking",
      detail: `${strongest.terminal || "Terminal"} pressure`,
    };
  }
  const text = String(preview.terminal || "");
  if (/severe|red|poor|high/i.test(text)) return { label: "High", detail: cleanLiveText(text, "Terminal pressure elevated") };
  if (/busy|moderate|amber|pressure/i.test(text)) return { label: "Moderate", detail: cleanLiveText(text, "Terminal pressure moderate") };
  if (/good|green|normal/i.test(text)) return { label: "Good", detail: cleanLiveText(text, "Terminal flow normal") };
  return { label: "Checking", detail: "Terminal pressure updating" };
};

const deriveQueueLiveStatus = (preview = {}) => {
  const queues = Array.isArray(preview.terminal_queues) ? preview.terminal_queues : [];
  const strongest = queues
    .slice()
    .sort((a, b) => statusRank(b?.label || b?.level) - statusRank(a?.label || a?.level))[0];
  if (strongest) {
    const wait = formatQueueLines(strongest.lines || []);
    return {
      label: strongest.label || strongest.level || "Checking",
      detail: cleanLiveText(`${strongest.terminal || "Queue"} • ${wait}`, "Queue snapshot updating"),
    };
  }
  const firstQueue = Array.isArray(preview.queues) ? preview.queues.find(Boolean) : "";
  if (/severe|red|45|60|busy/i.test(firstQueue)) return { label: "Busy", detail: cleanLiveText(firstQueue, "Queues busy") };
  if (/30|moderate|amber/i.test(firstQueue)) return { label: "Moderate", detail: cleanLiveText(firstQueue, "Queues moderate") };
  if (/good|green|0-5|5-10/i.test(firstQueue)) return { label: "Good", detail: cleanLiveText(firstQueue, "Queues look good") };
  return { label: "Checking", detail: cleanLiveText(firstQueue, "Security and border queues updating") };
};

const formatLiveAge = (value) => {
  if (!value) return "Auto-updated every 2 min";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Auto-updated every 2 min";
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 90) return "Just updated";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min old`;
  return `${Math.round(seconds / 3600)} hr old`;
};

const buildHeathrowLiveCards = (airport = fallbackHeathrowLive) => {
  const hero = airport.hero_status || {};
  const preview = airport.preview || {};
  const terminals = deriveTerminalLiveStatus(preview);
  const queues = deriveQueueLiveStatus(preview);
  const weatherDetail = hero.weather?.headline || hero.weather?.reason || preview.weather;

  return [
    { key: "road", icon: "▰", label: "Roads", value: hero.road?.label || "Checking", detail: hero.road?.headline || hero.road?.reason || "Road checks updating" },
    { key: "rail", icon: "▤", label: "Rail", value: hero.rail?.label || "Checking", detail: hero.rail?.headline || hero.rail?.reason || "Rail checks updating" },
    { key: "flights", icon: "✈", label: "Flights", value: hero.flights?.label || "Checking", detail: hero.flights?.headline || hero.flights?.reason || "Flight checks updating" },
    { key: "terminals", icon: "▦", label: "Terminals", value: terminals.label, detail: terminals.detail },
    { key: "queues", icon: "◫", label: "Queues", value: queues.label, detail: queues.detail },
    { key: "weather", icon: "☁", label: "Weather", value: hero.weather?.label || "Checking", detail: weatherDetail || "Weather checks updating" },
  ];
};

const asLiveList = (value, fallback = []) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return fallback.filter(Boolean);
};

const liveTextItem = (value, fallbackStatus = "Checking") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const parts = text.split(" • ").map((part) => part.trim()).filter(Boolean);
  const status = parts.length > 1 ? parts[parts.length - 1] : fallbackStatus;
  return {
    title: parts[0] || text || "Status checking",
    detail: parts.length > 2 ? parts.slice(1, -1).join(" • ") : "",
    status: /good|clear|on time|moderate|busy|watch|severe|high|delay|checking/i.test(status) ? status : fallbackStatus,
  };
};

const terminalPressureItems = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure.filter(Boolean) : [];
  if (structured.length) {
    return structured.slice(0, 4).map((item) => ({
      title: item.terminal || "Terminal",
      detail: `${Number(item.flights || 0)} flights • ${Number(item.delayed_30 || 0)} delayed 30m+`,
      status: item.label || item.level || "Checking",
    }));
  }
  return String(preview.terminal || "Terminals checking")
    .split("•")
    .map((item) => liveTextItem(item, "Checking"))
    .slice(0, 4);
};

const terminalQueueItems = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_queues) ? preview.terminal_queues.filter(Boolean) : [];
  if (structured.length) {
    return structured.slice(0, 4).map((item) => ({
      title: item.terminal || "Queue",
      detail: formatQueueLines(item.lines || []),
      status: item.label || item.level || "Checking",
    }));
  }
  return asLiveList(preview.queues, ["Security and border queue checks are updating."])
    .map((item) => liveTextItem(item, "Checking"))
    .slice(0, 4);
};

const detailedHeathrowGroups = (airport = fallbackHeathrowLive) => {
  const hero = airport.hero_status || {};
  const preview = airport.preview || {};
  return [
    {
      key: "road",
      title: "Road corridors",
      subtitle: "Major routes and visible traffic pressure",
      icon: "▰",
      status: hero.road?.label || "Checking",
      items: asLiveList(preview.road, [hero.road?.headline || hero.road?.reason || "Road checks updating"]).slice(0, 4).map((item) => liveTextItem(item, hero.road?.label || "Checking")),
    },
    {
      key: "flights",
      title: "Flights",
      subtitle: "Disruption, arrivals and departures",
      icon: "✈",
      status: hero.flights?.label || "Checking",
      items: [
        {
          title: "Current flight pressure",
          detail: hero.flights?.headline || hero.flights?.reason || "Flight checks updating",
          status: hero.flights?.label || "Checking",
        },
        {
          title: "Airport flow",
          detail: `${preview.arrivals || "0"} arrivals • ${preview.departures || "0"} departures`,
          status: hero.flights?.label || "Checking",
        },
        ...asLiveList(preview.flights).slice(0, 1).map((item) => liveTextItem(item, hero.flights?.label || "Checking")),
      ],
    },
    {
      key: "rail",
      title: "Rail and Underground",
      subtitle: "Airport stations, lines and route status",
      icon: "▤",
      status: hero.rail?.label || "Checking",
      items: asLiveList(preview.rail, [hero.rail?.headline || hero.rail?.reason || "Rail checks updating"]).slice(0, 4).map((item) => liveTextItem(item, hero.rail?.label || "Checking")),
    },
    {
      key: "terminals",
      title: "Terminal pressure",
      subtitle: "Flight delay and cancellation status by terminal",
      icon: "▦",
      status: deriveTerminalLiveStatus(preview).label,
      items: terminalPressureItems(preview),
    },
    {
      key: "queues",
      title: "Queue snapshot",
      subtitle: "Security and border waits by terminal",
      icon: "◫",
      status: deriveQueueLiveStatus(preview).label,
      items: terminalQueueItems(preview),
    },
    {
      key: "weather",
      title: "Weather at LHR",
      subtitle: "Weather included in leave-time checks",
      icon: "☁",
      status: hero.weather?.label || "Checking",
      items: [
        {
          title: preview.weather || "Weather checking",
          detail: hero.weather?.headline || hero.weather?.reason || "Live weather checks updating",
          status: hero.weather?.label || "Checking",
        },
      ],
    },
  ];
};

function normalizeHeathrowPlace(value = "") {
  const upper = value.toUpperCase();
  if (upper.includes("HEATHROW TERMINAL 5")) return "Heathrow Terminal 5";
  if (upper.includes("HEATHROW TERMINAL 4")) return "Heathrow Terminal 4";
  if (upper.includes("HEATHROW TERMINAL 3")) return "Heathrow Terminal 3";
  if (upper.includes("HEATHROW TERMINAL 2")) return "Heathrow Terminal 2";
  if (upper.includes("HEATHROW")) return value || "Heathrow Airport";
  return value;
}

function buildQuoteLocationString(text) {
  const cleanedText = String(text || "").trim();
  if (!cleanedText) return "";
  return extractPostcode(cleanedText);
}

function openSupportChat() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("ccg:open-support-chat", { detail: { flow: "enquiry" } }));
}

const PremiumLocationField = ({ ariaLabel, value, onChange, onPlaceSelected, placeholder, autoComplete }) => {
  return (
    <label className="minibus-v2-field minibus-v2-field-wide minibus-v2-location-field">
      <PostcodeInput
        value={value}
        onChange={onChange}
        onPlaceSelected={onPlaceSelected}
        placeholder={placeholder}
        restrictions={{ country: ["gb"] }}
        className="minibus-v2-location-input"
        style={{
          marginBottom: 0,
          height: "auto",
          minHeight: 0,
          padding: 0,
          border: 0,
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
        }}
        inputProps={{
          "aria-label": ariaLabel,
          autoComplete,
        }}
      />
    </label>
  );
};

const PremiumMinibusQuoteForm = () => {
  const navigate = useNavigate();
  const handoff = useMemo(() => new URLSearchParams(typeof window !== "undefined" ? window.location.search : ""), []);
  const initialDateTime = useMemo(() => localDatePartsInHours(25), []);
  const minDateTime = useMemo(() => localDatePartsInHours(4), []);
  const handoffPickupDate = useMemo(() => {
    const value = handoff.get("pickup_time");
    const parsed = value ? new Date(value) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) return initialDateTime;
    return { date: `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`, time: `${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}` };
  }, [handoff, initialDateTime]);
  const [pickup, setPickup] = useState(() => handoff.get("pickup") || "");
  const [dropoff, setDropoff] = useState(() => handoff.get("dropoff") || "");
  const [date, setDate] = useState(handoffPickupDate.date);
  const [time, setTime] = useState(handoffPickupDate.time);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const pickupTime = `${date}T${time}`;
  const minPickupTime = `${minDateTime.date}T${minDateTime.time}`;

  const enforceMinimumPickupTime = (nextDate, nextTime) => {
    const nextPickupTime = `${nextDate}T${nextTime}`;
    if (nextPickupTime < minPickupTime) {
      setDate(minDateTime.date);
      setTime(minDateTime.time);
      return;
    }
    setDate(nextDate);
    setTime(nextTime);
  };

  const getQuote = async () => {
    if (!pickup.trim() || !dropoff.trim()) {
      setError("Please enter both pickup and drop-off details.");
      return;
    }
    if (!date || !time) {
      setError("Please choose both pickup date and time.");
      return;
    }
    if (pickupTime < minPickupTime) {
      setError("Please choose a pickup time at least 4 hours from now.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.post("/api/get-quote-v2", {
        pickup: buildQuoteLocationString(pickup),
        dropoff: buildQuoteLocationString(dropoff),
        pickup_time: pickupTime,
        passengers: 8,
        bags: 0,
        ajp_journey_id: handoff.get("ajp_journey_id") || undefined,
        airport_code: handoff.get("airport_code") || undefined,
        terminal: handoff.get("terminal") || undefined,
        direction: handoff.get("direction") || undefined,
        flight_number: handoff.get("flight_number") || undefined,
        flight_time: handoff.get("flight_time") || undefined,
      });

      const data = res.data || {};
      if (data.quote_id && data.access_token) {
        const next = new URLSearchParams(handoff);
        next.set("vehicles", "nine,fourteen");
        navigate(`/minibus-results/${data.quote_id}/${data.access_token}?${next.toString()}`);
        return;
      }
      if (data.comingSoon) {
        setError("Sorry, we are not yet operating full coverage for this route. Please try another nearby pickup area.");
        return;
      }
      setError(data.error || "We could not calculate a minibus quote for this journey. Please check the details and try again.");
    } catch (err) {
      setError(err.response?.data?.error || "Our quote system is temporarily busy. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="minibus-v2-quote-card" id="minibusgrab-quote-form">
      <div className="minibus-v2-quote-head">
        <span>Get instant quotes</span>
        <small><i /> Free • Fast • No obligation</small>
      </div>

      <div className="minibus-v2-quote-grid">
        <PremiumLocationField
          ariaLabel="Pickup postcode or address"
          value={pickup}
          onChange={setPickup}
          onPlaceSelected={(place) => {
            const nextValue = place?.formatted_address || place?.name || "";
            setPickup(nextValue);
          }}
          placeholder="Enter pickup postcode or address"
          autoComplete="street-address"
        />

        <PremiumLocationField
          ariaLabel="Drop-off postcode or address"
          value={dropoff}
          onChange={setDropoff}
          onPlaceSelected={(place) => {
            const nextValue = normalizeHeathrowPlace(place?.formatted_address || place?.name || "");
            setDropoff(nextValue);
          }}
          placeholder="Enter dropoff postcode or address"
          autoComplete="street-address"
        />

        <label className="minibus-v2-field">
          <span className="minibus-v2-sr-only">Pickup date</span>
          <input
            aria-label="Pickup date"
            type="date"
            value={date}
            min={minDateTime.date}
            onChange={(event) => enforceMinimumPickupTime(event.target.value, time)}
          />
        </label>

        <label className="minibus-v2-field">
          <span className="minibus-v2-sr-only">Pickup time</span>
          <input
            aria-label="Pickup time"
            type="time"
            value={time}
            onChange={(event) => enforceMinimumPickupTime(date, event.target.value)}
          />
        </label>

        <div className="minibus-v2-quote-actions">
          <button type="button" className="minibus-v2-quote-btn" onClick={getQuote} disabled={loading}>
            {loading ? "Checking..." : "Get quotes now"} <span>→</span>
          </button>
          <button type="button" className="minibus-v2-clear-btn" onClick={openSupportChat}>
            Need help?
          </button>
          <small>Takes 20 seconds</small>
        </div>
      </div>

      {error ? <div className="minibus-v2-error">{error}</div> : null}

      <div className="minibus-v2-quote-promises">
        <span>Best price guarantee</span>
        <span>No hidden charges</span>
        <span>Free 60-min waiting</span>
        <span>Secure online booking</span>
        <span>Instant email confirmation</span>
      </div>

      <PremiumContactStrip title="Need help with your minibus quote?" />
    </div>
  );
};

const PremiumReviewsBar = () => (
  <section className="minibus-v2-reviews minibus-v2-premium-reviews" aria-label="Customer reviews and trust signals">
    <CcgPremiumReviews pageKey="/minibus-hire" />
  </section>
);

const PremiumHeathrowStatus = () => (
  <HeathrowLiveStatusCompact
    title="Heathrow (LHR)"
    kicker="Live airport intelligence"
    className="minibus-v2-status"
    footnote="Roads, flights, terminals, queues, rail and weather are checked before minibus journeys."
  />
);

const PremiumHeathrowDetailStatus = () => {
  const [airport, setAirport] = useState(fallbackHeathrowLive);

  useEffect(() => {
    let cancelled = false;

    const loadHeathrowStatus = async () => {
      try {
        const res = await api.get("/api/open/airport-intelligence-summary?airports=LHR");
        if (cancelled) return;
        const liveHeathrow = (res?.data?.airports || []).find((item) => String(item?.code || "").toUpperCase() === "LHR");
        setAirport(liveHeathrow || fallbackHeathrowLive);
      } catch (err) {
        if (!cancelled) setAirport(fallbackHeathrowLive);
      }
    };

    loadHeathrowStatus();
    const timer = setInterval(loadHeathrowStatus, 2 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const detailedGroups = detailedHeathrowGroups(airport);

  return (
      <section className="minibus-v2-lhr-detail" aria-label="Detailed Heathrow status preview">
        <div className="minibus-v2-content-header minibus-v2-lhr-detail-head">
          <span className="minibus-v2-live-kicker"><i /> Detailed LHR status</span>
          <h2>Heathrow journey checks before you book</h2>
          <p>Roads, rail, flights, terminals, queues and weather are grouped into a travel-ready preview for minibus journeys.</p>
        </div>
        <div className="minibus-v2-lhr-detail-grid">
          {detailedGroups.map((group) => {
            const tone = statusToneKey(group.status);
            return (
              <article className={`minibus-v2-lhr-detail-card is-${tone}`} key={group.key}>
                <div className="minibus-v2-lhr-detail-card-head">
                  <b>{group.icon}</b>
                  <span>
                    <strong>{group.title}</strong>
                    <small>{group.subtitle}</small>
                  </span>
                  <em>{group.status || "Checking"}</em>
                </div>
                <div className="minibus-v2-lhr-detail-list">
                  {group.items.map((item, index) => (
                    <div key={`${group.key}-${item.title}-${index}`}>
                      <span>
                        <strong>{cleanLiveText(item.title, "Status checking")}</strong>
                        <small>{cleanLiveText(item.detail, "Live check updating")}</small>
                      </span>
                      <em className={`is-${statusToneKey(item.status)}`}>{item.status || group.status || "Checking"}</em>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>
  );
};

const HomeMinibusGrab = () => {
  useEffect(() => {
    trackFunnelEvent("landing_view", {
      brand: "minibusgrab",
      source: "brand_home",
    });
  }, []);

  useEffect(() => {
    const title = "Heathrow Minibus Hire | 8 & 16 Seater Transfers | MinibusGrab";
    const description =
      "Book Heathrow minibus hire for 8 seater, 9 seater, 14 seater and 16 seater group transfers from Slough, Windsor, Burnham, Taplow, Maidenhead, Langley, Marlow and Henley. Fixed quotes, licensed operators and live LHR road, rail, flight, terminal and queue status.";
    const keywords =
      "Heathrow minibus hire, Heathrow minibus, Heathrow 8 seater, Heathrow 9 seater, 16 seater Heathrow, 14 seater Heathrow, airport minibus, London minibus hire, Slough to Heathrow minibus, Windsor to Heathrow minibus, Burnham to Heathrow minibus, Taplow to Heathrow minibus, Maidenhead to Heathrow minibus, Langley to Heathrow minibus, Marlow to Heathrow minibus, Henley to Heathrow minibus, group airport transfer, licensed minibus drivers, DBS checked drivers, PSV operator licence PF2071924";
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([key, value]) => {
        el[key] = value;
        if (typeof el.setAttribute === "function") el.setAttribute(key, value);
      });
    };

    document.title = title;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', {
      property: "og:description",
      content:
        "Premium Heathrow minibus quotes for 8, 9, 14 and 16 seater group transfers, backed by licensed operator trust signals and live LHR road, rail, flight, terminal and queue intelligence.",
    });
    setMeta('meta[property="og:url"]', { property: "og:url", content: "https://www.minibusgrab.com/" });
    setMeta('meta[property="og:image"]', { property: "og:image", content: "https://www.minibusgrab.com/minibus-hero.png" });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: "Heathrow Minibus Hire | MinibusGrab" });
    setMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: "Fixed Heathrow minibus quotes with live LHR status, licensed operator trust and 8 to 16 seater group travel options.",
    });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: "https://www.minibusgrab.com/minibus-hero.png" });
  }, []);

  const scrollToForm = () => {
    const el = document.getElementById("minibusgrab-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="home-page minibusgrab-home minibusgrab-v2-home">
      <Helmet>
        <title>Heathrow Minibus Hire | 8 & 16 Seater Transfers | MinibusGrab</title>
        <meta
          name="description"
          content="Book Heathrow minibus hire for 8 seater, 9 seater, 14 seater and 16 seater group transfers from Slough, Windsor, Burnham, Taplow, Maidenhead, Langley, Marlow and Henley. Fixed quotes, licensed operators and live LHR road, rail, flight, terminal and queue status."
        />
        <meta
          name="keywords"
          content="Heathrow minibus hire, Heathrow minibus, Heathrow 8 seater, Heathrow 9 seater, 16 seater Heathrow, 14 seater Heathrow, airport minibus, London minibus hire, Slough to Heathrow minibus, Windsor to Heathrow minibus, Burnham to Heathrow minibus, Taplow to Heathrow minibus, Maidenhead to Heathrow minibus, Langley to Heathrow minibus, Marlow to Heathrow minibus, Henley to Heathrow minibus, group airport transfer, licensed minibus drivers, DBS checked drivers, PSV operator licence PF2071924"
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.minibusgrab.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Heathrow Minibus Hire | 8 & 16 Seater Transfers | MinibusGrab" />
        <meta
          property="og:description"
          content="Premium Heathrow minibus quotes for 8, 9, 14 and 16 seater group transfers, backed by licensed operator trust signals and live LHR road, rail, flight, terminal and queue intelligence."
        />
        <meta property="og:url" content="https://www.minibusgrab.com/" />
        <meta property="og:image" content="https://www.minibusgrab.com/minibus-hero.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Heathrow Minibus Hire | MinibusGrab" />
        <meta
          name="twitter:description"
          content="Fixed Heathrow minibus quotes with live LHR status, licensed operator trust and 8 to 16 seater group travel options."
        />
        <meta name="twitter:image" content="https://www.minibusgrab.com/minibus-hero.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "@id": "https://www.minibusgrab.com/#website",
                name: "MinibusGrab",
                url: "https://www.minibusgrab.com/",
                description: "Heathrow minibus hire quotes for 8, 9, 14 and 16 seater airport transfers, London trips and UK group travel.",
                inLanguage: "en-GB",
              },
              {
                "@type": "Organization",
                "@id": "https://www.minibusgrab.com/#organization",
                name: "MinibusGrab",
                url: "https://www.minibusgrab.com/",
                logo: "https://www.minibusgrab.com/logo.png",
                sameAs: [
                  "https://find-and-update.company-information.service.gov.uk/company/10377899",
                  "https://www.gov.uk/find-vehicle-operators",
                ],
              },
              {
                "@type": "Organization",
                "@id": "https://www.minibusgrab.com/#licensed-operator",
                name: "RHP Minibus Hire UK Ltd",
                legalName: "RHP MINIBUS HIRE UK LIMITED",
                identifier: [
                  {
                    "@type": "PropertyValue",
                    propertyID: "Companies House number",
                    value: "10377899",
                  },
                  {
                    "@type": "PropertyValue",
                    propertyID: "PSV operator licence",
                    value: "PF2071924",
                  },
                ],
              },
              {
                "@type": "Service",
                "@id": "https://www.minibusgrab.com/#minibus-hire",
                name: "Heathrow minibus hire",
                alternateName: ["Heathrow 8 seater minibus", "Heathrow 16 seater minibus", "Heathrow group airport transfer"],
                serviceType: "Licensed minibus hire and group airport transfers with live Heathrow airport intelligence",
                provider: { "@id": "https://www.minibusgrab.com/#organization" },
                areaServed: [
                  "Heathrow Airport",
                  "Slough",
                  "Windsor",
                  "Burnham",
                  "Taplow",
                  "Maidenhead",
                  "Langley",
                  "Marlow",
                  "Henley-on-Thames",
                  "London",
                  "Gatwick Airport",
                  "Luton Airport",
                  "Stansted Airport",
                  "United Kingdom",
                ],
                hasOfferCatalog: {
                  "@type": "OfferCatalog",
                  name: "Heathrow minibus vehicle options",
                  itemListElement: [
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "8 passenger / 9-seater Heathrow minibus" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "14 to 16 passenger Heathrow minibus" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "25 seater mini coach by licensed PSV partner" } },
                  ],
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <section className="minibus-v2-hero">
        <div className="minibus-v2-hero-bg" aria-hidden="true" />
        <div className="minibus-v2-hero-inner">
          <div className="minibus-v2-hero-copy">
            <h1>
              Heathrow group transfers powered by <span>live intelligence</span>
            </h1>
            <p>
              Real-time airport, traffic and rail data helps us plan the best route, so your group arrives on time,
              every time.
            </p>

            <div className="minibus-v2-benefits">
              {heroBenefits.map((item) => (
                <div className="minibus-v2-benefit" key={item.title}>
                  <b>{item.icon}</b>
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.body}</small>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="minibus-v2-trust-badges" aria-label="Trust badges">
            {trustBadges.map((badge) => (
              <article className="minibus-v2-trust-badge" key={badge.eyebrow}>
                <b>{badge.icon}</b>
                <span>
                  <small>{badge.eyebrow}</small>
                  <strong>{badge.title}</strong>
                  <em>
                    {badge.href ? (
                      <a href={badge.href} target="_blank" rel="noopener noreferrer">{badge.body}</a>
                    ) : badge.body}
                  </em>
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="minibus-v2-quote-wrap">
        <PremiumMinibusQuoteForm />
      </div>

      <div className="minibus-v2-mobile-benefits" aria-label="Booking benefits">
        {heroBenefits.map((item) => (
          <div className="minibus-v2-benefit" key={item.title}>
            <b>{item.icon}</b>
            <span>
              <strong>{item.title}</strong>
              <small>{item.body}</small>
            </span>
          </div>
        ))}
      </div>

      <PremiumReviewsBar />

      <PremiumHeathrowStatus />

      <section className="minibus-v2-proof">
        <div className="minibus-v2-proof-intro">
          <h2>Your journey, <span>our responsibility</span></h2>
          <p>We’re a UK-registered operator with thousands of happy customers. Your group is in safe hands.</p>
        </div>
        {operatorCards.map((card) => (
          <article className="minibus-v2-proof-card" key={card.eyebrow}>
            <b>{card.icon}</b>
            <span>
              <small>{card.eyebrow}</small>
              <strong>{card.title}</strong>
              <em>
                {card.href ? (
                  <a href={card.href} target="_blank" rel="noopener noreferrer">{card.body}</a>
                ) : card.body}
              </em>
            </span>
          </article>
        ))}
      </section>

      <section className="minibus-v2-reasons">
        <div className="minibus-v2-reasons-title">
          <b>✦</b>
          <h2>Why groups choose us</h2>
        </div>
        {reasonCards.map((item) => (
          <article key={item.title}>
            <b>{item.icon}</b>
            <span>
              <strong>{item.title}</strong>
              <small>{item.body}</small>
            </span>
          </article>
        ))}
      </section>

      <PremiumHeathrowDetailStatus />

      <RoutePricePreview2 routeKey="default" vehicle="minibus" limit={4} title="Popular Heathrow minibus previews" />
      <RouteIntelligencePreview2 routeKey="default" compact />

      <section className="minibus-v2-content minibus-v2-editorial">
        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Built for group journeys</h2>
            <p>
              MinibusGrab is a dedicated front door for minibus travel: simple quote entry, group-sized vehicles
              and the same booking path after quote results.
            </p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {intelligenceCards.map((card) => (
              <article key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Licensed operator trust</h2>
            <p>
              Company details, licence references and operator checks are surfaced early so group transport feels clear before booking.
            </p>
          </div>
          <div className="minibus-v2-info-grid">
            {operatorTrustItems.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Popular Heathrow minibus routes</h2>
            <p>Use one quote flow for local Heathrow transfers, London travel and wider UK group movement.</p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {localHeathrowRoutes.map((route) => (
              <article className={route.href ? "minibus-v2-route-card is-linked" : "minibus-v2-route-card"} key={route.title}>
                <h3>
                  {route.href ? <Link to={route.href}>{route.title}</Link> : route.title}
                </h3>
                <p>Enter pickup, Heathrow terminal or airport drop-off and travel time to check 8 to 16 seater options for your group.</p>
                {route.href ? <Link className="minibus-v2-inline-link" to={route.href}>View route page</Link> : null}
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Airport, hotel and event minibus trips</h2>
            <p>Group transfers for Heathrow, Gatwick, Luton, Stansted, London hotels, family events and multi-stop journeys.</p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {tripTypes.map((trip) => (
              <article key={trip}>
                <h3>{trip}</h3>
                <p>Enter pickup, drop-off and travel time to check the right minibus options for your group.</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Vehicle options</h2>
            <p>Start with group-sized quote settings, then choose from 8 passenger, 14 passenger, 16 passenger or larger coach options where available.</p>
          </div>
          <div className="minibus-v2-info-grid">
            {fleetOptions.map((option) => (
              <article key={option.title}>
                <h3>{option.title}</h3>
                <p>{option.body}</p>
                {option.link ? <Link className="minibus-v2-inline-link" to={option.link}>View coach hire</Link> : null}
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Licensed drivers and professional standards</h2>
            <p>Group airport journeys are handled through licensed operators and professional driver standards for safe, punctual travel.</p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {driverTrustItems.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-platform-card">
          <div>
            <h2>Shared platform, separate brand</h2>
            <p>
              The backend, quote storage, booking flow and database stay unchanged. The MinibusGrab domain gives groups a minibus-first homepage,
              brand framing and quote entry.
            </p>
          </div>
          <button type="button" className="minibus-v2-quote-btn" onClick={scrollToForm}>
            Start a minibus quote <span>→</span>
          </button>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Minibus hire FAQs</h2>
            <p>Answers to common group-travel questions before you request a quote.</p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {faqItems.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Airports, hotels and attractions</h2>
            <p>Use MinibusGrab for practical group journeys where timing, luggage and vehicle size matter.</p>
          </div>
          <div className="minibus-v2-info-grid">
            {destinationGroups.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-support-card">
          <div>
            <h2>Need help planning group transport?</h2>
            <p>
              For complex routes, multi-stop journeys, child seats, larger luggage loads or coach-size groups,
              send the details and the team can advise on the right vehicle.
            </p>
          </div>
          <div className="minibus-v2-support-actions">
            <Link to="/contact">Contact group travel support</Link>
            <button type="button" onClick={scrollToForm}>Start with an online quote</button>
          </div>
        </div>
      </section>

      <button className="home-floating-cta minibus-v2-floating-cta" onClick={scrollToForm}>
        Get Minibus Quote
      </button>

      <FloatingSupportChat
        brand="minibusgrab"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={10000}
        nudgeText="Need minibus help?"
      />
    </div>
  );
};

export default HomeMinibusGrab;
